import { useState } from "react";
import { Button, Card, Drawer, Modal, Space, Spin, Timeline } from "antd";
import { CloseOutlined, CompressOutlined, ExpandAltOutlined } from "@ant-design/icons";
import { OriginPill, StatusPill } from "./StatusBadges";
import { formatCurrency, formatDateTime, formatPhone } from "../utils/format";
import {
  getImageTypeLabel,
  getSenderSiteLabel,
  getTypedCodeLabel,
} from "../utils/labels";
import { FLOW_TYPE_LABELS, formatCarrier } from "../constants";

function buildTimelineItems(pedido) {
  if (!pedido) return [];

  const events = [
    {
      key: "created",
      label: "Pedido criado",
      description: "Solicitação registrada no sistema.",
      value: pedido.createdAt,
      color: "blue",
    },
    {
      key: "confirmed",
      label: "Pagamento confirmado",
      description: "Reserva confirmada para depósito.",
      value: pedido.confirmedAt,
      color: "green",
    },
    pedido.status === "CONFIRMADO" || pedido.depositDueAt
      ? {
          key: "depositDue",
          label: "Prazo para depósito",
          description: "Janela limite para colocar o pacote no locker.",
          value: pedido.depositDueAt,
          color: "orange",
        }
      : null,
    {
      key: "deposited",
      label: "Objeto depositado",
      description: "Pacote colocado no locker.",
      value: pedido.depositedAt,
      color: "green",
    },
    {
      key: "retrieved",
      label: pedido.retrievedByDriverName
        ? `Retirado por ${pedido.retrievedByDriverName}`
        : "Objeto retirado",
      description: pedido.retrievedByDriverName
        ? "Motorista confirmou a retirada."
        : "Pacote retirado pelo motorista.",
      value: pedido.retrievedAt,
      color: "green",
    },
    {
      key: "rejected",
      label: "Pedido rejeitado",
      description: "Solicitação rejeitada no processamento.",
      value: pedido.rejectedAt,
      color: "red",
    },
    pedido.status === "EXPIRADO"
      ? {
          key: "expired",
          label: "Pedido expirado",
          description: "Prazo encerrado sem conclusão do depósito.",
          value: pedido.depositDueAt || pedido.updatedAt,
          color: "red",
        }
      : null,
  ].filter((event) => event?.value);

  return events.map((event) => ({
    color: event.color,
    children: (
      <div className="te-tracking-event">
        <div className="te-tracking-event__header">
          <strong>{event.label}</strong>
          <span className="te-muted">{formatDateTime(event.value)}</span>
        </div>
        {event.description ? <div className="te-muted">{event.description}</div> : null}
      </div>
    ),
  }));
}

export function PedidoDrawer({ open, pedido, loading, onClose }) {
  const reserva = pedido?.reserva;
  const imagens = reserva?.imagens || [];
  const [expanded, setExpanded] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  function handleClose() {
    setExpanded(false);
    setPreviewImage(null);
    onClose?.();
  }

  return (
    <>
      <Drawer
        title={
          <div className="te-order-drawer-title">
            <Button
              type="text"
              icon={<CloseOutlined />}
              aria-label="Fechar detalhes"
              onClick={handleClose}
            />
            <Button
              type="text"
              icon={expanded ? <CompressOutlined /> : <ExpandAltOutlined />}
              aria-label={expanded ? "Recolher detalhes" : "Expandir detalhes"}
              onClick={() => setExpanded((v) => !v)}
            />
            <strong>{pedido ? `Pedido #${pedido.orderNumber || "-"}` : "Detalhes do pedido"}</strong>
          </div>
        }
        placement="right"
        width={expanded ? "100vw" : 460}
        className={expanded ? "te-order-drawer te-order-drawer--expanded" : "te-order-drawer"}
        open={open}
        closable={false}
        onClose={handleClose}
      >
        {loading ? (
          <div className="te-empty"><Spin /></div>
        ) : pedido ? (
          <div className={expanded ? "te-order-detail-grid" : "te-stack"}>
            <Card className="te-card te-order-summary-card">
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <OriginPill origin={pedido.origin} />
                  <StatusPill status={pedido.status} />
                </div>
                <div><strong>Cliente:</strong> {pedido.user?.fullName || "-"}</div>
                <div><strong>E-mail:</strong> {pedido.user?.email || "-"}</div>
                <div><strong>Telefone:</strong> {formatPhone(pedido.user?.phone)}</div>
                <div><strong>Locker:</strong> {pedido.colleto?.name || "-"}</div>
                <div>
                  <strong>Reserva:</strong>{" "}
                  {pedido.reservationId || reserva?.reservationId || "-"}
                </div>
                <div><strong>Locker:</strong> {reserva?.LockerAssignment?.locker || reserva?.lockerId || "-"}</div>
                <div><strong>Tamanho:</strong> {reserva?.lockerSize || "-"}</div>
                <div><strong>Valor:</strong> {formatCurrency(pedido.valor)}</div>
                <div><strong>Retirado por:</strong> {pedido.retrievedByDriverName || "-"}</div>
                {pedido.retrievedByDriverEmail ? (
                  <div><strong>E-mail do motorista:</strong> {pedido.retrievedByDriverEmail}</div>
                ) : null}
              </Space>
            </Card>

            <Card className="te-card te-order-flow-card">
              <h3 style={{ margin: 0, marginBottom: 12, color: "var(--te-primary)" }}>
                Fluxo da reserva
              </h3>
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                {reserva?.flowType ? (
                  <div>
                    <strong>Tipo:</strong>{" "}
                    {FLOW_TYPE_LABELS[reserva.flowType] || reserva.flowType}
                  </div>
                ) : null}
                {formatCarrier(reserva) ? (
                  <div>
                    <strong>Transportadora:</strong> {formatCarrier(reserva)}
                  </div>
                ) : null}
                {reserva?.senderSite ? (
                  <div>
                    <strong>{getSenderSiteLabel(pedido)}:</strong> {reserva.senderSite}
                  </div>
                ) : null}
                {reserva?.code ? (
                  <div>
                    <strong>{getTypedCodeLabel(pedido)}:</strong> {reserva.code}
                  </div>
                ) : null}
                {reserva?.qrCode ? (
                  <div><strong>QR / referência:</strong> {reserva.qrCode}</div>
                ) : null}
              </Space>
            </Card>

            <Card className="te-card te-order-tracking-card">
              <h3 style={{ margin: 0, marginBottom: 12, color: "var(--te-primary)" }}>
                Percurso logístico
              </h3>
              {buildTimelineItems(pedido).length === 0 ? (
                <div className="te-empty">Nenhum evento registrado ainda.</div>
              ) : (
                <Timeline className="te-tracking-timeline" items={buildTimelineItems(pedido)} />
              )}
            </Card>

            <Card className="te-card te-order-period-card">
              <h3 style={{ margin: 0, marginBottom: 12, color: "var(--te-primary)" }}>
                Período da reserva
              </h3>
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <div><strong>Início:</strong> {formatDateTime(reserva?.periodStart)}</div>
                <div><strong>Fim:</strong> {formatDateTime(reserva?.periodEnd)}</div>
              </Space>
            </Card>

            <Card className="te-card te-order-images-card">
              <h3 style={{ margin: 0, marginBottom: 12, color: "var(--te-primary)" }}>
                Imagens do pacote
              </h3>
              {imagens.length > 0 ? (
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                  {imagens.map((img) => {
                    const label = getImageTypeLabel(pedido, img.type);
                    return (
                      <div key={img.id}>
                        <div style={{ fontWeight: 700, color: "var(--te-primary)", marginBottom: 8 }}>
                          {label}
                        </div>
                        <img
                          src={img.base64}
                          alt={label}
                          onClick={() => setPreviewImage({ src: img.base64, title: label })}
                          style={{
                            width: "100%",
                            borderRadius: 14,
                            border: "1px solid var(--te-border, #DBE8F1)",
                            background: "var(--te-surface-muted, #F4F8FB)",
                            cursor: "zoom-in",
                          }}
                        />
                      </div>
                    );
                  })}
                </Space>
              ) : (
                <div className="te-empty">Nenhuma imagem vinculada a esta reserva.</div>
              )}
            </Card>
          </div>
        ) : null}
      </Drawer>

      <Modal
        open={!!previewImage}
        footer={null}
        onCancel={() => setPreviewImage(null)}
        centered
        width={820}
        title={previewImage?.title || "Imagem"}
      >
        {previewImage ? (
          <img
            src={previewImage.src}
            alt={previewImage.title}
            style={{
              width: "100%",
              maxHeight: "75vh",
              objectFit: "contain",
              borderRadius: 16,
              background: "var(--te-surface-muted, #F4F8FB)",
            }}
          />
        ) : null}
      </Modal>
    </>
  );
}
